import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../services/api.service';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { toUtcIsoDate } from '../utils/date-utils';

@Component({
  standalone: true,
  selector: 'app-salesinvoice',
  templateUrl: './salesinvoice.component.html',
  styleUrl: './salesinvoice.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatExpansionModule,
    MatTableModule,
    MatCheckboxModule
  ]
})
export class SalesInvoiceComponent implements OnInit {
  salesInvoiceForm: FormGroup;
  unitTypes: string[] = [];
  orderStatuses: any[] = [];
  paymentTermsList: any[] = [];
  errorMessage: string = '';
  customerContacts: any[] = [];
  customerAddresses: any[] = [];
  customers: any[] = [];
  lotNumsList: any[] = [];
  filteredCustomers!: Observable<any[]>;
  filteredLotNumsList: Observable<any[]>[] = [];
  salesInvoiceId: number | null = null;
  isEditMode: boolean = false;
  salesOrders: any[] = [];
  salesOrderColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  purchaseOrders: any[] = [];
  purchaseOrderColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  proformas: any[] = [];
  proformaColumns = ['number', 'date', 'status', 'totalAmount', 'actions'];
  bankDetails: any = null;
  remitToAddress: any = null;

  constructor(
    private fb: FormBuilder, 
    private router: Router, 
    private route: ActivatedRoute,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {
    this.salesInvoiceForm = this.fb.group({
      // Invoice specific properties
      qboId: [null],
      invoiceNumber: ['', Validators.required],
      invoiceDate: [new Date(), Validators.required],
      dueDate: [null],
      status: ['Draft'],
      shipDate: [null],
      shipper: [''],
      salesPerson: [''],
      
      // Customer Information
      customerId: [null, Validators.required],
      customerSearch: [''],
      shippingAddressId: [null],
      billingAddressId: [null],
      customerContactId: [null],
      customerPO: [''],
      shipAttentionTo: [''],
      
      // Related Sales Order (optional)
      salesOrderId: [null],
      
      // Financial Information
      subTotal: [0, [Validators.min(0)]],
      discountAmount: [0, [Validators.min(0)]],
      taxAmount: [0, [Validators.min(0)]],
      shippingAmount: [0, [Validators.min(0)]],
      handlingFee: [0, [Validators.min(0)]],
      totalAmount: [0, [Validators.min(0)]],
      amountPaid: [0, [Validators.min(0)]],
      
      // Payment Information
      paymentTerms: [''],
      paymentMethod: [''],
      
      // Additional Information
      notes: [''],
      
      // Bank Details
      bankDetails: this.fb.group({
        accountName: [''],
        bankName: [''],
        accountNumber: [''],
        routingNumber: [''],
        swiftCode: [''],
        fedwireABA: [''],
        ibanCode: [''],
        bankAddress: [''],
        isActive: [true],
        notes: ['']
      }),
      
      // Remit To Address
      remitToAddress: this.fb.group({
        addressLine1: [''],
        addressLine2: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: ['']
      }),
      
      // Products array
      products: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.loadUnitTypes();
    this.loadOrderStatuses();
    this.loadPaymentTerms();
    this.loadCustomers();
    this.loadLotNums();
    
    // Check if there's an id parameter in the route
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id && !isNaN(+id)) {
        this.salesInvoiceId = +id;
        this.isEditMode = false;
        this.loadSalesInvoice(this.salesInvoiceId);
        this.loadRelatedSalesOrders(this.salesInvoiceId);
      } else {
        this.isEditMode = true;
        this.loadBankDetails();
        this.loadRemitToAddress();
        this.loadLatestOrderNumber();
      }
    });
    
    // Setup customer search autocomplete
    this.filteredCustomers = this.salesInvoiceForm.get('customerSearch')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCustomers(value || ''))
    );
    
    // Watch for customerId changes to load related contacts and addresses
    this.salesInvoiceForm.get('customerId')?.valueChanges.subscribe(customerId => {
      if (customerId) {
        // Clear previously selected contact and address values
        this.salesInvoiceForm.patchValue({
          customerContactId: null,
          shippingAddressId: null,
          billingAddressId: null
        }, { emitEvent: false });
        
        // Load new contacts and addresses for the selected customer
        this.loadCustomerContacts(customerId);
        this.loadCustomerAddresses(customerId);
      } else {
        this.customerContacts = [];
        this.customerAddresses = [];
        this.salesInvoiceForm.patchValue({
          customerContactId: null,
          shippingAddressId: null,
          billingAddressId: null
        }, { emitEvent: false });
      }
    });

    // Pre-populate from navigation state (e.g. generated from Sales Order)
    const prefill = history.state?.prefill;
    if (prefill) {

      if (!this.salesOrders.find(so => so.id === prefill.salesOrder?.id)) {
        this.salesOrders.push(prefill.salesOrder);
      }

      if (prefill.purchaseOrders?.length) {
        prefill.purchaseOrders.forEach((po: any) => {
          if (!this.purchaseOrders.find((p: any) => p.id === po.id)) {
            this.purchaseOrders.push(po);
          }
        });
      }

      this.salesInvoiceForm.patchValue({
        customerId: prefill.customerId || null,
        customerContactId: prefill.customerContactId || null,
        shippingAddressId: prefill.shippingAddressId || null,
        billingAddressId: prefill.billingAddressId || null,
        customerPO: prefill.customerPO || null,
        shipAttentionTo: prefill.shipAttentionTo || null,
        shipper: prefill.shipper || null,
        salesPerson: prefill.salesPerson || null,
        paymentTerms: prefill.paymentTerms || null,
        discountAmount: prefill.discountAmount || 0,
        taxAmount: prefill.taxAmount || 0,
        shippingAmount: prefill.shippingAmount || 0,
        handlingFee: prefill.handlingFee || 0,
        ...(prefill.customerSearch ? { customerSearch: prefill.customerSearch } : {})
      }, { emitEvent: false });

      if (prefill.customerId) {
        this.loadCustomerContacts(prefill.customerId);
        this.loadCustomerAddresses(prefill.customerId);
      }

      // Load products if available
        const productsData = prefill.products || prefill.Products || [];
        productsData.forEach((product: any) => {

          this.products.push(this.fb.group({
            lotNumId:          [product.lotNumId     || product.LotNumId     || '', Validators.required],
            lotNumSearch:      [''],
            productId:         [''],
            productName:       [''],
            catalogNum:        [''],
            unitPrice:         [product.unitPrice    || product.UnitPrice    || 0],
            unitType:          [product.unitType     || product.UnitType     || ''],
            quantity:          [product.quantity     || product.Quantity     || 1],
            quantityOnHand:    [null],
            lineNumber:        [product.lineNumber   || product.LineNumber   || 0],
            discountPercent:   [0],
            discountAmount:    [0],
            taxRate:           [0],
            taxAmount:         [0],
            description:       [''],
            notes:             [''],
            taxCode:           [''],
            desiredPackaging:  [product.desiredPackaging || product.DesiredPackaging || '']
          }));
          this.setupLotNumAutocomplete(this.products.length - 1);
        });

        this.enrichLineItems();

    }
  }

  enterEditMode(): void {
    this.isEditMode = true;
    this.setSelectsDisabled(false);

    const formValue = this.salesInvoiceForm.value;
    if (formValue.customerId > 0) {
      this.loadCustomerContacts(formValue.customerId);
      this.loadCustomerAddresses(formValue.customerId);
    }
  }

  private showError(message: string, err?: any): void {
    let detail: string | undefined;
    if (err?.error) {
      if (typeof err.error === 'string') {
        detail = err.error;
      } else {
        detail = err.error.detail || err.error.details || err.error.message || err.error.title || err.error.error;
      }
    }
    if (!detail && err?.statusText && err.statusText !== 'OK') {
      detail = `(${err.status}: ${err.statusText})`;
    }
    this.errorMessage = detail ? `${message} ${detail}` : message;
    this.cdr.detectChanges();
  }

  private setSelectsDisabled(disabled: boolean): void {
    const controls = ['status', 'shippingAddressId', 'billingAddressId', 'customerContactId', 'paymentTerms', 'paymentMethod'];
    controls.forEach(ctrl => {
      const control = this.salesInvoiceForm.get(ctrl);
      disabled ? control?.disable({ emitEvent: false }) : control?.enable({ emitEvent: false });
    });
    this.products.controls.forEach(product => {
      const unitType = product.get('unitType');
      disabled ? unitType?.disable({ emitEvent: false }) : unitType?.enable({ emitEvent: false });
    });
  }

  loadSalesInvoice(id: number): void {
    while (this.products.length) this.products.removeAt(0);
    this.apiService.get<any>(`invoice/${id}`).subscribe({
      next: (data) => {
        // Handle both camelCase and PascalCase from API
        const salesInvoice = {
          qboId: data.qboId || data.QboId || data.QBO_Id || null,
          invoiceNumber: data.number || data.Number || '',
          invoiceDate: data.date || data.Date ? new Date(data.date || data.Date) : new Date(),
          dueDate: data.dueDate || data.DueDate ? new Date(data.dueDate || data.DueDate) : null,
          status: data.status || data.Status || 'Draft',
          customerId: data.customerId || data.CustomerId || null,
          shippingAddressId: data.shippingAddressId || data.ShippingAddressId || null,
          billingAddressId: data.billingAddressId || data.BillingAddressId || null,
          customerContactId: data.customerContactId || data.CustomerContactId || null,
          customerPO: data.customerPO || data.CustomerPO || '',
          salesOrderId: data.salesOrderId || data.SalesOrderId || null,
          subTotal: data.subTotal || data.SubTotal || 0,
          discountAmount: data.discountAmount || data.DiscountAmount || 0,
          taxAmount: data.taxAmount || data.TaxAmount || 0,
          shippingAmount: data.shippingAmount || data.ShippingAmount || 0,
          handlingFee: data.handlingFee || data.HandlingFee || 0,
          totalAmount: data.totalAmount || data.TotalAmount || 0,
          amountPaid: data.amountPaid || data.AmountPaid || 0,
          paymentTerms: data.paymentTerms || data.PaymentTerms || '',
          paymentMethod: data.paymentMethod || data.PaymentMethod || '',
          notes: data.externalNotes || data.ExternalNotes || '',
          shipDate: data.shipDate || data.ShipDate ? new Date(data.shipDate || data.ShipDate) : null,
          shipper: data.shipper || data.Shipper || '',
          salesPerson: data.salesPerson || data.SalesPerson || '',
          shipAttentionTo: data.shipAttentionTo || data.ShipAttentionTo || ''
        };
        
        // Check if API response includes nested customer contact and address objects
        // bankdetails and remittoaddress are handled separately below since they are nested objects rather than arrays
        const customerContact = data.customerContact || data.CustomerContact;
        const customerAddress = data.customerAddress || data.CustomerAddress;
        const customerObj = data.customer || data.Customer;
        const bankDetailsObj = data.bankDetails || data.BankDetails;
        const remitToAddressObj = data.remitToAddress || data.RemitToAddress;
        
        // If nested objects exist, populate the arrays directly
        if (customerContact) {
          this.customerContacts = [customerContact];
        }
        if (customerAddress) {
          this.customerAddresses = [customerAddress];
        }

        if (bankDetailsObj) {
          this.bankDetails = bankDetailsObj;
          this.salesInvoiceForm.patchValue({
            bankDetails: {
              accountName: bankDetailsObj.accountName || bankDetailsObj.AccountName || '',
              bankName: bankDetailsObj.bankName || bankDetailsObj.BankName || '',
              accountNumber: bankDetailsObj.accountNumber || bankDetailsObj.AccountNumber || '',
              routingNumber: bankDetailsObj.routingNumber || bankDetailsObj.RoutingNumber || '',
              swiftCode: bankDetailsObj.swiftCode || bankDetailsObj.SwiftCode || '',
              fedwireABA: bankDetailsObj.fedwireABA || bankDetailsObj.FedwireABA || '',
              ibanCode: bankDetailsObj.ibanCode || bankDetailsObj.IBANCode || '',
              bankAddress: bankDetailsObj.bankAddress || bankDetailsObj.BankAddress || '',
              isActive: bankDetailsObj.isActive !== undefined ? bankDetailsObj.isActive : (bankDetailsObj.IsActive !== undefined ? bankDetailsObj.IsActive : true),
              notes: bankDetailsObj.notes || bankDetailsObj.Notes || ''
            }
          }, { emitEvent: false });
        }

        if (remitToAddressObj) {
          this.remitToAddress = remitToAddressObj;
          this.salesInvoiceForm.patchValue({
            remitToAddress: {
              addressLine1: remitToAddressObj.addressLine1 || remitToAddressObj.AddressLine1 || '',
              addressLine2: remitToAddressObj.addressLine2 || remitToAddressObj.AddressLine2 || '',
              city: remitToAddressObj.city || remitToAddressObj.City || '',
              state: remitToAddressObj.state || remitToAddressObj.State || '',
              postalCode: remitToAddressObj.postalCode || remitToAddressObj.PostalCode || '',
              country: remitToAddressObj.country || remitToAddressObj.Country || ''
            }
          }, { emitEvent: false });
        }
        
        // Patch form values without triggering valueChanges for customerId
        this.salesInvoiceForm.patchValue(salesInvoice, { emitEvent: false });
        
        // Set the customer for the search field display
        if (salesInvoice.customerId) {
          // First try to use nested customer object from API
          if (customerObj) {
            this.salesInvoiceForm.patchValue({ customerSearch: customerObj }, { emitEvent: false });
          } else {
            // Otherwise, try to find from loaded customers array or fetch it
            const customer = this.customers.find(c => (c.id || c.Id) === salesInvoice.customerId);
            if (customer) {
              this.salesInvoiceForm.patchValue({ customerSearch: customer }, { emitEvent: false });
            } else {
              // Load the specific customer if not found in the list
              this.apiService.get<any>(`customer/${salesInvoice.customerId}`).subscribe({
                next: (customerData) => {
                  this.salesInvoiceForm.patchValue({ customerSearch: customerData }, { emitEvent: false });
                },
                error: (err) => console.error('Error loading customer:', err)
              });
            }
          }
        }
        
        // If nested data wasn't provided, trigger the load methods manually
        if (salesInvoice.customerId && (!customerContact || !customerAddress)) {
          if (!customerContact) {
            this.loadCustomerContacts(salesInvoice.customerId);
          }
          if (!customerAddress) {
            this.loadCustomerAddresses(salesInvoice.customerId);
          }
        }
        
        // Load bank details if available
        if (data.bankDetails || data.BankDetails) {
          const bd = data.bankDetails || data.BankDetails;
          this.salesInvoiceForm.patchValue({
            bankDetails: {
              accountName: bd.accountName || bd.AccountName || '',
              bankName: bd.bankName || bd.BankName || '',
              accountNumber: bd.accountNumber || bd.AccountNumber || '',
              routingNumber: bd.routingNumber || bd.RoutingNumber || '',
              swiftCode: bd.swiftCode || bd.SwiftCode || '',
              fedwireABA: bd.fedwireABA || bd.FedwireABA || '',
              ibanCode: bd.ibanCode || bd.IBANCode || '',
              bankAddress: bd.bankAddress || bd.BankAddress || '',
              isActive: bd.isActive !== undefined ? bd.isActive : (bd.IsActive !== undefined ? bd.IsActive : true),
              notes: bd.notes || bd.Notes || ''
            }
          }, { emitEvent: false });
        }
        
        // Load remit to address if available
        if (data.remitToAddress || data.RemitToAddress) {
          const rta = data.remitToAddress || data.RemitToAddress;
          this.salesInvoiceForm.patchValue({
            remitToAddress: {
              addressLine1: rta.addressLine1 || rta.AddressLine1 || '',
              addressLine2: rta.addressLine2 || rta.AddressLine2 || '',
              city: rta.city || rta.City || '',
              state: rta.state || rta.State || '',
              postalCode: rta.postalCode || rta.PostalCode || '',
              country: rta.country || rta.Country || ''
            }
          }, { emitEvent: false });
        }
        
        // Load products if available
        const productsData = data.products || data.Products || [];
        productsData.forEach((product: any) => {
          this.products.push(this.fb.group({
            id:                [product.id || product.Id || 0],
            lotNumId:          [product.lotNumId     || product.LotNumId     || '', Validators.required],
            lotNumSearch:      [''],
            productId:         [product.productId    || product.ProductId    || ''],
            productName:       [''],
            catalogNum:        [product.catalogNum   || product.CatalogNum   || ''],
            unitPrice:         [product.unitPrice    || product.UnitPrice    || 0],
            unitType:          [product.unitType     || product.UnitType     || ''],
            quantity:          [product.quantity     || product.Quantity     || 1],
            quantityOnHand:    [null],
            lineNumber:        [product.lineNumber   || product.LineNumber   || 0],
            discountPercent:   [product.discountPercent || product.DiscountPercent || 0],
            discountAmount:    [product.discountAmount  || product.DiscountAmount  || 0],
            taxRate:           [product.taxRate      || product.TaxRate      || 0],
            taxAmount:         [product.taxAmount    || product.TaxAmount    || 0],
            description:       [product.description  || product.Description  || ''],
            notes:             [product.notes        || product.Notes        || ''],
            taxCode:           [product.taxCode      || product.TaxCode      || ''],
            desiredPackaging:  [product.desiredPackaging || product.DesiredPackaging || '']
          }));
          this.setupLotNumAutocomplete(this.products.length - 1);
        });
        this.enrichLineItems();
        this.setSelectsDisabled(true);
      },
      error: (error) => {
        console.error('Error loading sales invoice:', error);
        this.showError('Failed to load sales invoice. Please try again.', error);
      }
    });
  }

  loadUnitTypes(): void {
    this.apiService.get<string[]>('product/unitTypes').subscribe({
      next: (data) => {
        this.unitTypes = data;
      },
      error: (err) => {
        console.error('Error loading unit types:', err);
        // Fallback to default values
        this.unitTypes = [];
      }
    });
  }

  loadOrderStatuses(): void {
    this.apiService.get<string[]>('misc/orderStatuses').subscribe({
      next: (data) => {
        this.orderStatuses = data;
      },
      error: (err) => {
        console.error('Error loading order statuses:', err);
        this.orderStatuses = [];
      }
    });
  }

  loadLatestOrderNumber(): void {
    this.apiService.get<any>(`invoice/latest-number`).subscribe({
      next: (data) => {
        this.salesInvoiceForm.patchValue({ invoiceNumber: data.orderNumber }, { emitEvent: false });
      },
      error: (err) => {
        console.error('Error loading latest order number:', err);
        this.salesInvoiceForm.patchValue({ invoiceNumber: '' }, { emitEvent: false });
      }
    });
  }

  loadPaymentTerms(): void {
    this.apiService.get<string[]>('misc/paymentTerms').subscribe({
      next: (data) => {
        this.paymentTermsList = data || [];
        if (!this.salesInvoiceId && !this.salesInvoiceForm.get('paymentTerms')?.value) {
          const defaultTerm = this.paymentTermsList.find((t: any) => (t.name || t.Name) === 'Net30');
          if (defaultTerm) {
            this.salesInvoiceForm.patchValue({ paymentTerms: defaultTerm.name || defaultTerm.Name }, { emitEvent: false });
          }
        }
      },
      error: (err) => {
        console.error('Error loading payment terms:', err);
        this.paymentTermsList = [];
      }
    });
  }

  loadCustomers(): void {
    this.apiService.get<any[]>('customer/all').subscribe({
      next: (data) => {
        this.customers = data || [];
      },
      error: (err) => {
        console.error('Error loading customers:', err);
        this.customers = [];
      }
    });
  }

  private _filterCustomers(value: string): any[] {
    if (typeof value !== 'string') return this.customers;
    const filterValue = value?.toLowerCase();
    return this.customers.filter(customer => {
      const name = customer.name || customer.Name || '';
      const id = (customer.id || customer.Id || '').toString();
      return name?.toLowerCase().includes(filterValue) || id.includes(filterValue);
    });
  }

  displayCustomer(customer: any): string {
    return customer ? `${customer.name || customer.Name || ''} (ID: ${customer.id || customer.Id || ''})` : '';
  }

  onCustomerSelected(customer: any): void {
    if (customer) {
      const customerId = customer.id || customer.Id;
      this.salesInvoiceForm.patchValue({ customerId: customerId });
    }
  }

  clearCustomer(): void {
    this.salesInvoiceForm.patchValue({
      customerSearch: '',
      customerId: null,
      customerContactId: null,
      shippingAddressId: null,
      billingAddressId: null
    });
    this.customerContacts = [];
    this.customerAddresses = [];
  }

  loadCustomerContacts(customerId: string | number): void {
    this.apiService.get<any[]>(`contact/customer/all/${customerId}`).subscribe({
      next: (data) => {
        this.customerContacts = data || [];
        // Manually trigger change detection to update the mat-select dropdown
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading customer contacts:', err);
        this.customerContacts = [];
      }
    });
  }

  loadCustomerAddresses(customerId: string | number): void {
    this.apiService.get<any[]>(`address/customer/all/${customerId}`).subscribe({
      next: (data) => {
        this.customerAddresses = data || [];
        // Manually trigger change detection to update the mat-select dropdowns
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading customer addresses:', err);
        this.customerAddresses = [];
      }
    });
  }

  loadLotNums(): void {
    this.apiService.get<any[]>('product/lotnums/all').subscribe({
      next: (data) => {
        this.lotNumsList = data || [];
        this.enrichLineItems();
      },
      error: (err) => {
        console.error('Error loading lot numbers:', err);
        this.lotNumsList = [];
      }
    });
  }

  private enrichLineItems(): void {
    if (!this.lotNumsList.length) return;
    this.products.controls.forEach((control) => {
      const lotNumId = control.get('lotNumId')?.value;
      if (lotNumId) {
        const found = this.lotNumsList.find(ln => (ln.id || ln.Id) == lotNumId);
        if (found) {
          control.patchValue({
            lotNumSearch:      found,
            productId:         found.productId || found.ProductId || found.vendorProduct?.product?.id || found.product?.id || found.product?.Id || '',
            productName:       found.vendorProduct?.product?.name || found.vendorProduct?.product?.Name || found.product?.name || found.product?.Name || '',
            catalogNum:        found.vendorProduct?.product?.catalogNum || found.vendorProduct?.product?.CatalogNum || found.product?.catalogNum || found.product?.CatalogNum || '',
            unitType:          found.unitType || found.UnitType || control.get('unitType')?.value || '',
            quantityOnHand:    found.quantityOnHand ?? found.QuantityOnHand ?? null,
            // Preserve an existing line-item description (e.g. loaded from a saved invoice)
            // and only fall back to the master product description when none has been set yet.
            description:       control.get('description')?.value || found.vendorProduct?.product?.description || found.vendorProduct?.product?.Description || found.product?.description || found.product?.Description || ''
          }, { emitEvent: false });
        }
      }
    });
  }

  setupLotNumAutocomplete(index: number): void {
    const control = this.products.at(index).get('lotNumSearch')!;
    this.filteredLotNumsList[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map(value => this._filterLotNums(value || ''))
    );
  }

  _filterLotNums(value: string | any): any[] {
    if (typeof value !== 'string') return this.lotNumsList;
    const f = value.toLowerCase();
    return this.lotNumsList.filter(ln => {
      const num        = (ln.number  || ln.Number  || '').toLowerCase();
      const catalog    = (ln.vendorProduct?.product?.catalogNum || ln.vendorProduct?.product?.CatalogNum || ln.product?.catalogNum || ln.product?.CatalogNum || '').toLowerCase();
      const name       = (ln.vendorProduct?.product?.name || ln.vendorProduct?.product?.Name || ln.product?.name || ln.product?.Name || '').toLowerCase();
      const vendorName = (ln.vendorProduct?.vendor?.name || ln.vendorProduct?.vendor?.Name || '').toLowerCase();
      return num.includes(f) || catalog.includes(f) || name.includes(f) || vendorName.includes(f);
    });
  }

  displayLotNum(lotNum: any): string {
    return lotNum ? (lotNum.number || lotNum.Number || '') : '';
  }

  onLotNumSelected(index: number, lotNum: any): void {
    const lotNumId         = lotNum.id || lotNum.Id;
    const productId        = lotNum.productId || lotNum.ProductId || lotNum.vendorProduct?.product?.id || lotNum.product?.id || lotNum.product?.Id || '';
    const catalogNum       = lotNum.vendorProduct?.product?.catalogNum || lotNum.vendorProduct?.product?.CatalogNum || lotNum.product?.catalogNum || lotNum.product?.CatalogNum || '';
    const productName      = lotNum.vendorProduct?.product?.name || lotNum.vendorProduct?.product?.Name || lotNum.product?.name || lotNum.product?.Name || '';
    const unitType         = lotNum.unitType || lotNum.UnitType || '';
    const quantityOnHand   = lotNum.quantityOnHand ?? lotNum.QuantityOnHand ?? null;
    const description      = lotNum.vendorProduct?.product?.description || lotNum.vendorProduct?.product?.Description || lotNum.product?.description || lotNum.product?.Description || '';
    this.products.at(index).patchValue({ lotNumId, productId, catalogNum, productName, unitType, quantityOnHand, description });
  }

  clearLotNum(index: number): void {
    this.products.at(index).patchValue({
      lotNumSearch: '', lotNumId: '', productId: '', catalogNum: '', productName: '',
      quantityOnHand: null, description: ''
    });
  }

  // Getter for products FormArray
  get products(): FormArray {
    return this.salesInvoiceForm.get('products') as FormArray;
  }

  // Get descriptive title for a product line item
  getProductTitle(index: number): string {
    const item = this.products.at(index).value;
    const quantity = item.quantity || 0;
    const unitType = item.unitType || 'each';
    const unitPrice = item.unitPrice || 0;
    const total = this.calculateLineTotalWithTax(index);
    const catalogNum = item.catalogNum || '';
    const search = item.lotNumSearch;
    const lotNumStr = search
      ? (typeof search === 'object' ? (search.number || search.Number || '') : String(search))
      : (item.lotNumId ? `Lot ${item.lotNumId}` : '');
    const productName = item.productName || (search && typeof search === 'object'
      ? (search.product?.name || search.product?.Name || '')
      : '');
    const label = lotNumStr
      ? (productName ? `${lotNumStr} — ${productName}` : lotNumStr)
      : (catalogNum || '');
    if (label && quantity) {
      return `${label} - ${quantity} ${unitType} @ $${unitPrice} = ${total}`;
    } else if (label) {
      return label;
    }
    return `Product Line ${index + 1}`;
  }

  // Create a new product line item FormGroup
  createProductLineItem(): FormGroup {
    return this.fb.group({
      lotNumId:          ['', Validators.required],
      lotNumSearch:      [''],
      productId:         [''],
      productName:       [''],
      catalogNum:        [''],
      unitPrice:         [0, [Validators.required, Validators.min(0)]],
      unitType:          [''],
      quantity:          [1, [Validators.required, Validators.min(0.01)]],
      quantityOnHand:    [null],
      lineNumber:        [this.products.length + 1],
      discountPercent:   [0, [Validators.min(0), Validators.max(100)]],
      discountAmount:    [0, [Validators.min(0)]],
      taxRate:           [0, [Validators.min(0)]],
      taxAmount:         [0, [Validators.min(0)]],
      description:       [''],
      notes:             [''],
      taxCode:           [''],
      desiredPackaging:  ['']
    });
  }

  // Add a new product line item
  addProduct(): void {
    this.products.push(this.createProductLineItem());
    this.setupLotNumAutocomplete(this.products.length - 1);
  }

  // Remove a product line item
  removeProduct(index: number): void {
    this.products.removeAt(index);
    // Update line numbers after removal
    this.updateLineNumbers();
  }

  // Update line numbers for all products
  updateLineNumbers(): void {
    this.products.controls.forEach((control, index) => {
      control.get('lineNumber')?.setValue(index + 1);
    });
  }

  // Calculate line total for a product (UnitPrice × Quantity)
  calculateLineTotal(index: number): number {
    const product = this.products.at(index);
    const unitPrice = product.get('unitPrice')?.value || 0;
    const quantity = product.get('quantity')?.value || 0;
    return unitPrice * quantity;
  }

  // Check whether the entered quantity exceeds the available quantity for a product line item
  isQuantityExceeded(index: number): boolean {
    const product = this.products.at(index);
    const quantityOnHand = product.get('quantityOnHand')?.value;
    if (quantityOnHand === null || quantityOnHand === undefined) return false;
    const quantity = product.get('quantity')?.value || 0;
    return quantity > quantityOnHand;
  }

  // Calculate net amount for a product (LineTotal - DiscountAmount)
  calculateNetAmount(index: number): number {
    const lineTotal = this.calculateLineTotal(index);
    const product = this.products.at(index);
    const discountAmount = product.get('discountAmount')?.value || 0;
    return lineTotal - discountAmount;
  }

  // Calculate line total with tax (NetAmount + TaxAmount)
  calculateLineTotalWithTax(index: number): number {
    const netAmount = this.calculateNetAmount(index);
    const product = this.products.at(index);
    const taxAmount = product.get('taxAmount')?.value || 0;
    return netAmount + taxAmount;
  }

  // Calculate subtotal for all products
  calculateSubTotal(): number {
    let subTotal = 0;
    for (let i = 0; i < this.products.length; i++) {
      subTotal += this.calculateLineTotal(i);
    }
    return subTotal;
  }

  // Calculate total after discounts
  calculateTotalAfterDiscounts(): number {
    let total = 0;
    for (let i = 0; i < this.products.length; i++) {
      total += this.calculateNetAmount(i);
    }
    const invoiceDiscountAmount = this.salesInvoiceForm.get('discountAmount')?.value || 0;
    return total - invoiceDiscountAmount;
  }

  // Calculate total for invoice (SubTotal - Discounts + Taxes + Shipping + Handling Fee)
  calculateTotal(): number {
    const subTotal = this.calculateSubTotal();
    const discountAmount = this.salesInvoiceForm.get('discountAmount')?.value || 0;
    const taxAmount = this.salesInvoiceForm.get('taxAmount')?.value || 0;
    const shippingAmount = this.salesInvoiceForm.get('shippingAmount')?.value || 0;
    const handlingFee = this.salesInvoiceForm.get('handlingFee')?.value || 0;
    return subTotal - discountAmount + taxAmount + shippingAmount + handlingFee;
  }

  // Calculate balance (TotalAmount - AmountPaid)
  calculateBalance(): number {
    const totalAmount = this.calculateTotal();
    const amountPaid = this.salesInvoiceForm.get('amountPaid')?.value || 0;
    return totalAmount - amountPaid;
  }

  onSubmit() {
    if (this.salesInvoiceForm.valid) {
      const formValue = this.salesInvoiceForm.value;
      
      // Prepare data for API (convert to PascalCase for C# backend)
      var salesInvoiceData: any = {
        QBO_Id: formValue.qboId,
        Number: formValue.invoiceNumber,
        Date: toUtcIsoDate(formValue.invoiceDate),
        DueDate: toUtcIsoDate(formValue.dueDate),
        Status: formValue.status,
        CustomerId: formValue.customerId,
        ShippingAddressId: formValue.shippingAddressId,
        BillingAddressId: formValue.billingAddressId,
        CustomerContactId: formValue.customerContactId,
        CustomerPO: formValue.customerPO,
        SalesOrderId: formValue.salesOrderId,
        SubTotal: this.calculateSubTotal(),
        DiscountAmount: formValue.discountAmount,
        TaxAmount: formValue.taxAmount,
        ShippingAmount: formValue.shippingAmount,
        HandlingFee: formValue.handlingFee,
        TotalAmount: this.calculateTotal(),
        AmountPaid: formValue.amountPaid,
        PaymentTerms: formValue.paymentTerms,
        PaymentMethod: formValue.paymentMethod,
        ExternalNotes: formValue.notes,
        ShipDate: toUtcIsoDate(formValue.shipDate),
        Shipper: formValue.shipper,
        SalesPerson: formValue.salesPerson,
        ShipAttentionTo: formValue.shipAttentionTo,
        BankDetailsId: this.bankDetails?.id || this.bankDetails?.Id || null,
        BankDetails: formValue.bankDetails ? {
          AccountName: formValue.bankDetails.accountName,
          BankName: formValue.bankDetails.bankName,
          AccountNumber: formValue.bankDetails.accountNumber,
          RoutingNumber: formValue.bankDetails.routingNumber,
          SwiftCode: formValue.bankDetails.swiftCode,
          FedwireABA: formValue.bankDetails.fedwireABA,
          IBANCode: formValue.bankDetails.ibanCode,
          BankAddress: formValue.bankDetails.bankAddress,
          IsActive: formValue.bankDetails.isActive,
          Notes: formValue.bankDetails.notes
        } : null,
        RemitToId: this.remitToAddress?.id || this.remitToAddress?.Id || null,
        RemitToAddress: formValue.remitToAddress ? {
          AddressLine1: formValue.remitToAddress.addressLine1,
          AddressLine2: formValue.remitToAddress.addressLine2,
          City: formValue.remitToAddress.city,
          State: formValue.remitToAddress.state,
          PostalCode: formValue.remitToAddress.postalCode,
          Country: formValue.remitToAddress.country
        } : null,
        Products: formValue.products.map((p: any) => ({
          Id: p.id || p.Id || 0,
          LotNumId: p.lotNumId || null,
          ProductId: p.productId,
          UnitPrice: p.unitPrice,
          ...(formValue.status === 'Draft' && !p.unitType ? {} : { UnitType: p.unitType }),
          Quantity: p.quantity,
          LineNumber: p.lineNumber,
          DiscountPercent: p.discountPercent,
          DiscountAmount: p.discountAmount,
          TaxRate: p.taxRate,
          TaxAmount: p.taxAmount,
          Description: p.description,
          Notes: p.notes,
          TaxCode: p.taxCode,
          DesiredPackaging: p.desiredPackaging
        })),
        PurchaseOrders: this.purchaseOrders.map((po: any) => ({
          ...(po.id ? { Id: po.id } : {}),
        })),
        SalesOrders: this.salesOrders.map((so: any) => ({
          ...(so.id ? { Id: so.id } : {}),
        })),
        // Proformas: this.proformas.map((pf: any) => ({
        //   ...(pf.id ? { Id: pf.id } : {}),
        // }))
      };
      
      if (this.salesInvoiceId) {
        salesInvoiceData.Id = this.salesInvoiceId;
        this.apiService.put<any>(`invoice/${this.salesInvoiceId}`, salesInvoiceData).subscribe({
          next: () => this.router.navigate(['/sales-invoices']),
          error: (err) => {
            console.error('Error updating sales invoice:', err);
            this.showError('Failed to save sales invoice. Please try again.', err);
          }
        });
      } else {
        this.apiService.post<any>('invoice', salesInvoiceData).subscribe({
          next: () => this.router.navigate(['/sales-invoices']),
          error: (err) => {
            console.error('Error creating sales invoice:', err);
            this.showError('Failed to create sales invoice. Please try again.', err);
          }
        });
      }
    }
  }

  onCancel() {
    if (this.salesInvoiceId) {
      this.isEditMode = false;
      this.loadSalesInvoice(this.salesInvoiceId);
    } else {
      this.router.navigate(['/sales-invoices']);
    }
  }

  downloadPdf(): void {
    this.apiService.getBlob(`invoice/${this.salesInvoiceId}/pdf`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.salesInvoiceForm.get('invoiceNumber')?.value || this.salesInvoiceId;
        a.download = `${number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading PDF:', err);
        this.showError('Failed to generate PDF. Please try again.', err);
      }
    });
  }

  downloadPackingSlipPdf(): void {
    this.apiService.getBlob(`invoice/${this.salesInvoiceId}/packingslip/pdf`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.salesInvoiceForm.get('invoiceNumber')?.value || this.salesInvoiceId;
        a.download = `PackingSlip-${number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading packing slip PDF:', err);
        this.showError('Failed to generate packing slip PDF. Please try again.', err);
      }
    });
  }

  downloadLabels(): void {
    this.apiService.getBlob(`invoice/${this.salesInvoiceId}/labels`).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const number = this.salesInvoiceForm.get('invoiceNumber')?.value || this.salesInvoiceId;
        a.download = `${number}-labels.docx`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading labels:', err);
        this.showError('Failed to generate labels. Please try again.', err);
      }
    });
  }

  loadRelatedSalesOrders(id: number): void {
    this.apiService.get<any[]>(`invoice/${id}/salesorders`).subscribe({
      next: (data) => {
        const raw: any[] = data || [];
        this.salesOrders = raw.map((so: any) => ({
          id: so.id || so.Id,
          number: so.number || so.Number || '',
          date: so.date || so.Date ? new Date(so.date || so.Date) : null,
          status: so.status || so.Status || '',
          totalAmount: so.totalAmount || so.TotalAmount || 0
        }));
        // Extract purchase orders nested within each sales order and deduplicate
        const poMap = new Map<number, any>();
        raw.forEach((so: any) => {
          const pos: any[] = so.purchaseOrders || so.PurchaseOrders || [];
          pos.forEach((po: any) => {
            const poId = po.id || po.Id;
            if (!poMap.has(poId)) {
              poMap.set(poId, {
                id: poId,
                number: po.number || po.Number || '',
                date: po.date || po.Date ? new Date(po.date || po.Date) : null,
                status: po.status || po.Status || '',
                totalAmount: po.totalAmount || po.TotalAmount || 0
              });
            }
          });
        });
        this.purchaseOrders = Array.from(poMap.values());

        // Extract proformas: top-level data and nested in each sales order
        const pfMap = new Map<number, any>();
        const addProforma = (pf: any) => {
          const pfId = pf.id || pf.Id;
          if (pfId && !pfMap.has(pfId)) {
            pfMap.set(pfId, {
              id: pfId,
              number: pf.proformaNumber || pf.ProformaNumber || pf.number || pf.Number || '',
              date: pf.proformaDate || pf.ProformaDate || pf.date || pf.Date
                ? new Date(pf.proformaDate || pf.ProformaDate || pf.date || pf.Date)
                : null,
              status: pf.status || pf.Status || '',
              totalAmount: pf.totalAmount || pf.TotalAmount || 0
            });
          }
        };
        raw.forEach((so: any) => {
          (so.proformas || so.Proformas || []).forEach(addProforma);
        });
        this.proformas = Array.from(pfMap.values());

        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading related sales orders:', err)
    });
  }

  viewSalesOrder(id: number): void {
    this.router.navigate(['/orders', id]);
  }

  viewPurchaseOrder(id: number): void {
    this.router.navigate(['/purchase-orders', id]);
  }

  viewProforma(id: number): void {
    this.router.navigate(['/proformas', id]);
  }

  loadBankDetails(): void {
    this.apiService.get<any>('invoice/bankdetails').subscribe({
      next: (data) => {
        this.bankDetails = data;
        if ( this.bankDetails) {
          this.salesInvoiceForm.patchValue({
            bankDetails: {
              accountName:  this.bankDetails.accountName ||  this.bankDetails.AccountName || '',
              bankName:  this.bankDetails.bankName ||  this.bankDetails.BankName || '',
              accountNumber:  this.bankDetails.accountNumber ||  this.bankDetails.AccountNumber || '',
              routingNumber:  this.bankDetails.routingNumber ||  this.bankDetails.RoutingNumber || '',
              swiftCode: this.bankDetails.swiftCode || this.bankDetails.SwiftCode || '',
              fedwireABA: this.bankDetails.fedwireABA || this.bankDetails.FedwireABA || '',
              ibanCode: this.bankDetails.ibanCode || this.bankDetails.IBANCode || '',
              bankAddress: this.bankDetails.bankAddress || this.bankDetails.BankAddress || '',
              isActive: this.bankDetails.isActive !== undefined ? this.bankDetails.isActive : (this.bankDetails.IsActive !== undefined ? this.bankDetails.IsActive : true),
              notes: this.bankDetails.notes || this.bankDetails.Notes || ''
            }
          }, { emitEvent: false });
        }
      },
      error: (err) => {
        console.error('Error loading bank details:', err);
        this.bankDetails = null;
      }
    });
  }

  loadRemitToAddress(): void {
    this.apiService.get<any>('invoice/remittoaddress').subscribe({
      next: (data) => {
        this.remitToAddress = data;
        if (this.remitToAddress) {
          this.salesInvoiceForm.patchValue({
            remitToAddress: {
              addressLine1: this.remitToAddress.addressLine1 || this.remitToAddress.AddressLine1 || '',
              addressLine2: this.remitToAddress.addressLine2 || this.remitToAddress.AddressLine2 || '',
              city: this.remitToAddress.city || this.remitToAddress.City || '',
              state: this.remitToAddress.state || this.remitToAddress.State || '',
              postalCode: this.remitToAddress.postalCode || this.remitToAddress.PostalCode || '',
              country: this.remitToAddress.country || this.remitToAddress.Country || ''
            }
          }, { emitEvent: false });
        }


      },
      error: (err) => {
        console.error('Error loading remit to address:', err);
        this.remitToAddress = null;
      }
    });
  }

  formatAddress(address: any): string {
    const parts = [
      address.addressLine1 || address.AddressLine1,
      address.addressLine2 || address.AddressLine2,
      address.addressLine3 || address.AddressLine3,
      address.addressLine4 || address.AddressLine4,
      address.addressLine5 || address.AddressLine5,
      address.city || address.City,
      address.state || address.State,
      address.postalCode || address.PostalCode,
      address.country || address.Country
    ].filter(Boolean);
    return parts.join(', ') || '-';
  }

  getAddressTooltip(id: any, addresses: any[]): string {
    if (!id) return '';
    const found = addresses.find(a => (a.id || a.Id) == id);
    return found ? this.formatAddress(found) : '';
  }
}
